import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot, doc } from 'firebase/firestore';
import './AdminDashboard.css';

const TestComparisonsListPage = () => {
  const [allGenerations, setAllGenerations] = useState([]);
  const [parentGenerations, setParentGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  
  // Real-time update states
  const [listeningToRealtime, setListeningToRealtime] = useState(false);
  const [newGenerationIds, setNewGenerationIds] = useState(new Set());
  
  // Refs for managing listeners
  const mainListenerRef = useRef(null);
  const generationListenersRef = useRef(new Map());
  const generationsMapRef = useRef(new Map());

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    const checkAdmin = async () => {
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdTokenResult();
        setIsAdmin(idToken.claims.admin === true);
      }
    };
    checkAdmin();
  }, []);

  // Function to process generations and find parent-child relationships
  const processGenerations = (generations) => {
    // Create a map for quick lookup
    const genMap = new Map();
    generations.forEach(gen => {
      genMap.set(gen.id, gen);
      generationsMapRef.current.set(gen.id, gen);
    });

    // Find parent generations (those that have test children)
    const parentGens = generations.filter(gen => 
      !gen.isSystemTest && 
      generations.some(testGen => testGen.parentGenerationId === gen.id)
    );

    // For each parent, count its test children
    const parentsWithCounts = parentGens.map(parent => {
      const testChildren = generations.filter(gen => 
        gen.parentGenerationId === parent.id && gen.isSystemTest
      );
      return {
        ...parent,
        testCount: testChildren.length,
        completedTests: testChildren.filter(t => t.status === 'openai_processing_complete').length,
        failedTests: testChildren.filter(t => t.status.includes('error') || t.status.includes('failed')).length,
        testChildren: testChildren // Keep references to children for updates
      };
    });

    return parentsWithCounts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  // Function to fetch generation details including signed URLs
  const fetchGenerationDetails = async (generationId) => {
    try {
      const functions = getFunctions();
      const getDetails = httpsCallable(functions, 'getGenerationDetailsForAdmin');
      const result = await getDetails({ generationId });
      return result.data;
    } catch (error) {
      console.error(`Error fetching details for ${generationId}:`, error);
      return null;
    }
  };

  // Function to fetch generations with pagination
  const fetchGenerations = async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const functions = getFunctions();
      const getAllGenerations = httpsCallable(functions, 'getAllGenerationsForAdmin');
      
      const result = await getAllGenerations({ 
        page: page, 
        limit: ITEMS_PER_PAGE,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      const newGenerations = result.data.generations;
      const totalItems = result.data.total || 0;
      const calculatedTotalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
      
      setTotalPages(calculatedTotalPages);
      setHasMoreData(page < calculatedTotalPages);

      // Fetch detailed data for parent generations to get signed URLs
      const detailedGenerations = await Promise.all(
        newGenerations.map(async (gen) => {
          if (!gen.isSystemTest && gen.generatedImagePath) {
            const details = await fetchGenerationDetails(gen.id);
            return details || gen;
          }
          return gen;
        })
      );

      if (append) {
        setAllGenerations(prev => [...prev, ...detailedGenerations]);
      } else {
        setAllGenerations(detailedGenerations);
      }

      setCurrentPage(page);
      setLoading(false);
      setIsLoadingMore(false);
    } catch (err) {
      console.error('Error fetching generations:', err);
      setError(err.message);
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Load more data
  const loadMore = () => {
    if (!isLoadingMore && hasMoreData) {
      fetchGenerations(currentPage + 1, true);
    }
  };

  // Setup real-time listeners
  const setupRealtimeListeners = () => {
    if (mainListenerRef.current) {
      mainListenerRef.current();
    }

    const generationsRef = collection(db, 'GenerationRequests');
    const q = query(generationsRef, orderBy('createdAt', 'desc'), limit(10));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const newGen = { id: change.doc.id, ...change.doc.data() };
          
          // Check if it's a new generation (not in our initial load)
          if (!generationsMapRef.current.has(change.doc.id)) {
            setNewGenerationIds(prev => new Set([...prev, change.doc.id]));
            
            // Add to all generations
            setAllGenerations(prev => {
              const updated = [newGen, ...prev];
              return updated;
            });

            // Setup listener for this specific document
            setupDocumentListener(change.doc.id);
          }
        }
      });
    });

    mainListenerRef.current = unsubscribe;
    setListeningToRealtime(true);
  };

  // Setup listener for individual document updates
  const setupDocumentListener = (docId) => {
    if (generationListenersRef.current.has(docId)) {
      return;
    }

    const docRef = doc(db, 'GenerationRequests', docId);
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const updatedData = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Update in all generations
        setAllGenerations(prev => prev.map(gen => 
          gen.id === docId ? updatedData : gen
        ));

        // Update in the map
        generationsMapRef.current.set(docId, updatedData);
      }
    });

    generationListenersRef.current.set(docId, unsubscribe);
  };

  // Process generations whenever allGenerations changes
  useEffect(() => {
    const processed = processGenerations(allGenerations);
    setParentGenerations(processed);
  }, [allGenerations]);

  // Initial load and setup listeners
  useEffect(() => {
    if (isAdmin) {
      fetchGenerations();
      setupRealtimeListeners();
    }

    // Cleanup function
    return () => {
      if (mainListenerRef.current) {
        mainListenerRef.current();
        mainListenerRef.current = null;
      }

      generationListenersRef.current.forEach(unsubscribe => unsubscribe());
      generationListenersRef.current.clear();
    };
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="error-message">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading test comparisons...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard test-comparisons-list">
      <div className="dashboard-header">
        <h1>Test Comparisons</h1>
        <p>View all prompt tests running in parallel</p>
        <Link to="/admin/dashboard" className="back-link">&larr; Back to Dashboard</Link>
      </div>

      {/* Real-time updates notification */}
      {listeningToRealtime && newGenerationIds.size > 0 && (
        <div className="realtime-notification">
          <i className="ti ti-bell-ringing"></i>
          <span>{newGenerationIds.size} new generation{newGenerationIds.size > 1 ? 's' : ''} added</span>
        </div>
      )}

      {parentGenerations.length === 0 ? (
        <div className="empty-state">
          <h3>No test comparisons found</h3>
          <p>Test comparisons will appear here when prompts with "testInParallel" enabled are used.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="generations-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Activity</th>
                  <th>Main Prompt</th>
                  <th>Test Count</th>
                  <th>Status</th>
                  <th>User</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {parentGenerations.map((parent) => (
                  <React.Fragment key={parent.id}>
                    <tr className="generation-row">
                      <td>{new Date(parent.createdAt).toLocaleString()}</td>
                      <td>
                        <div className="activity-info">
                          {parent.stravaStats?.activityName && (
                            <div className="activity-name">{parent.stravaStats.activityName}</div>
                          )}
                          {parent.stravaId && (
                            <div className="activity-id">ID: {parent.stravaId}</div>
                          )}
                        </div>
                      </td>
                      <td>{parent.promptTemplateId || parent.prompt || 'Custom'}</td>
                      <td>
                        <div className="test-count-info">
                          <div>{parent.testCount} tests</div>
                          {parent.completedTests > 0 && (
                            <div className="test-status-breakdown">
                              <span className="status-success">{parent.completedTests} complete</span>
                              {parent.failedTests > 0 && (
                                <span className="status-error">{parent.failedTests} failed</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${parent.status.includes('complete') ? 'status-success' : parent.status.includes('error') || parent.status.includes('failed') ? 'status-error' : 'status-processing'}`}>
                          {parent.status}
                        </span>
                      </td>
                      <td>
                        <div className="user-info">
                          <div className="user-name">
                            {parent.user?.displayName || parent.user?.email || 'Unknown'}
                          </div>
                          <div className="user-email">{parent.user?.email}</div>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Link to={`/admin/compare/${parent.id}`} className="view-button" target="_blank" rel="noopener noreferrer">
                            View Comparison
                          </Link>
                        </div>
                      </td>
                    </tr>
                    <tr className="image-comparison-row">
                      <td colSpan="7">
                        <div className="inline-comparison-grid">
                          <div className="comparison-item">
                            <h5>Original</h5>
                            {parent.generatedImageUrl ? (
                              <img 
                                src={parent.generatedImageUrl} 
                                alt="Original generation"
                                className="inline-comparison-image"
                              />
                            ) : (
                              <div className="no-image-inline">No image</div>
                            )}
                          </div>
                          {parent.testChildren && parent.testChildren.map((test) => (
                            <div key={test.id} className="comparison-item">
                              <h5>{test.testPromptName || 'Test'}</h5>
                              {test.generatedImageUrl ? (
                                <img 
                                  src={test.generatedImageUrl} 
                                  alt={test.testPromptName}
                                  className="inline-comparison-image"
                                />
                              ) : (
                                <div className="no-image-inline">
                                  {test.status === 'openai_processing_complete' ? 'No image' : `Status: ${test.status}`}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {hasMoreData && (
            <div className="pagination-controls" style={{ textAlign: 'center', marginTop: '20px' }}>
              <button 
                onClick={loadMore} 
                className="load-more-button"
                disabled={isLoadingMore}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                  opacity: isLoadingMore ? 0.7 : 1
                }}
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </button>
              <p style={{ marginTop: '10px', color: '#6b6b6b', fontSize: '14px' }}>
                Showing {parentGenerations.length} test comparisons
                {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </p>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .test-comparisons-list {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-top: 30px;
        }

        .empty-state h3 {
          color: #495057;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #6c757d;
        }

        .test-count-info {
          font-size: 14px;
        }

        .test-status-breakdown {
          display: flex;
          gap: 8px;
          margin-top: 4px;
          font-size: 12px;
        }

        .test-status-breakdown .status-success {
          color: #166534;
        }

        .test-status-breakdown .status-error {
          color: #991B1B;
        }

        .realtime-notification {
          background: #EEF2FF;
          border: 1px solid #C7D2FE;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #4F46E5;
          font-size: 14px;
        }

        .realtime-notification i {
          font-size: 18px;
        }

        .image-comparison-row {
          background: #f8f9fa;
          border-top: none;
        }

        .image-comparison-row td {
          padding: 20px;
        }

        .inline-comparison-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          max-width: 100%;
        }

        .comparison-item {
          text-align: center;
        }

        .comparison-item h5 {
          margin: 0 0 10px 0;
          color: #495057;
          font-size: 14px;
          font-weight: 600;
        }

        .inline-comparison-image {
          width: 100%;
          max-width: 200px;
          height: auto;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .no-image-inline {
          width: 100%;
          max-width: 200px;
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e9ecef;
          border-radius: 8px;
          color: #6c757d;
          font-size: 14px;
          margin: 0 auto;
        }

        .generation-row {
          border-bottom: 1px solid #dee2e6;
        }

        .generation-row:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
};

export default TestComparisonsListPage;